/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import { useEffect, useState, useRef, memo, type MemoExoticComponent } from 'react';
import { css, jsx } from '@compiled/react';
import { ScreenReaderText } from '../../common/ui/screenreader-text/index.tsx';
import type { LiveMode } from '../../common/ui/types.tsx';

type Props = {
	message: string;
	liveMode?: LiveMode;
	shouldAnnounce?: boolean;
	testId?: string;
};

const NON_BREAKING_SPACE = '\u00A0';

export const AnnouncerV2: MemoExoticComponent<
	({ message, liveMode, shouldAnnounce, testId }: Props) => JSX.Element
> = memo(({ message, liveMode = 'polite', shouldAnnounce = true, testId }: Props): JSX.Element => {
	const previousMessage = useRef('');
	const setAlternatePolite = useRef(false);
	const addSuffix = useRef(false);

	const [oldMessage, setOldMessage] = useState('');
	const [newMessage, setNewMessage] = useState('');

	useEffect(() => {
		let messageToAnnounce = shouldAnnounce ? message : NON_BREAKING_SPACE;
		if (previousMessage.current === messageToAnnounce) {
			addSuffix.current = !addSuffix.current;
			if (addSuffix.current) {
				messageToAnnounce += NON_BREAKING_SPACE;
			}
		}
		setOldMessage(setAlternatePolite.current ? '' : messageToAnnounce);
		setNewMessage(setAlternatePolite.current ? messageToAnnounce : '');
		if (shouldAnnounce) {
			setAlternatePolite.current = !setAlternatePolite.current;
		}
		previousMessage.current = messageToAnnounce;
	}, [message, shouldAnnounce]);

	// 2 aria-live containers needed to prevent bugs in browsers that will stop
	// announcing updates to the same div if these are too repetitive.
	return (
		<div data-testid={testId} css={positionRelative}>
			<ScreenReaderText
				role="status"
				aria-live={liveMode}
				aria-relevant="additions text"
				aria-atomic="true"
			>
				{oldMessage}
			</ScreenReaderText>
			<ScreenReaderText
				role="status"
				aria-live={liveMode}
				aria-relevant="additions text"
				aria-atomic="true"
			>
				{newMessage}
			</ScreenReaderText>
		</div>
	);
});

const positionRelative = css({
	position: 'relative',
});
